import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import Button from '../../../components/ui/Button';
import Card, { CardContent } from '../../../components/ui/Card';
import Modal, {
  ModalHeader,
  ModalContent,
  ModalFooter,
} from '../../../components/ui/Modal';
import FormInput from '../../../components/ui/FormInput';
import { physicalBenefitsService, type PhysicalBenefit, type CreatePhysicalBenefitPayload, type DeliveryStatus } from '../../../services/physicalBenefitsService';
import { creatorService } from '../../../services/creatorService';

const DELIVERY_STATUS_COLORS: Record<DeliveryStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  SHIPPED: 'bg-blue-100 text-blue-700',
  IN_TRANSIT: 'bg-purple-100 text-purple-700',
  DELIVERED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
};

const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  PENDING: 'Pending',
  SHIPPED: 'Shipped',
  IN_TRANSIT: 'In Transit',
  DELIVERED: 'Delivered',
  FAILED: 'Failed',
};

const PhysicalBenefitsPage: React.FC = () => {
  const [benefits, setBenefits] = useState<PhysicalBenefit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState<PhysicalBenefit | null>(null);
  const [formData, setFormData] = useState<CreatePhysicalBenefitPayload>({
    membershipId: '',
    benefitName: '',
    description: '',
    shippingAddress: {
      name: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    },
  });
  const [updateData, setUpdateData] = useState({
    trackingNumber: '',
    carrier: '',
    deliveryStatus: 'PENDING' as DeliveryStatus,
    confirmationNote: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creatorId, setCreatorId] = useState<string | null>(null);

  useEffect(() => {
    loadCreatorProfile();
  }, []);

  useEffect(() => {
    if (creatorId) {
      loadBenefits();
    }
  }, [creatorId]);

  const loadCreatorProfile = async () => {
    try {
      const res = await creatorService.getMyCreatorProfile();
      if (res.creatorProfile?.id) {
        setCreatorId(res.creatorProfile.id);
      }
    } catch (error) {
      console.error('Failed to load creator profile:', error);
    }
  };

  const loadBenefits = async () => {
    if (!creatorId) return;
    try {
      setLoading(true);
      const res = await physicalBenefitsService.getPhysicalBenefits(creatorId);
      setBenefits(res.physicalBenefits || []);
    } catch (error: any) {
      console.error('Failed to load physical benefits:', error);
      alert(error?.response?.data?.message || 'Failed to load physical benefits');
    } finally {
      setLoading(false);
    }
  };


  const handleCreate = () => {
    setFormData({
      membershipId: '',
      benefitName: '',
      description: '',
      shippingAddress: {
        name: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      },
    });
    setSelectedBenefit(null);
    setShowCreateModal(true);
  };

  const handleEdit = (benefit: PhysicalBenefit) => {
    setUpdateData({
      trackingNumber: benefit.trackingNumber || '',
      carrier: benefit.carrier || '',
      deliveryStatus: benefit.deliveryStatus,
      confirmationNote: benefit.confirmationNote || '',
    });
    setSelectedBenefit(benefit);
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this physical benefit?')) return;

    try {
      await physicalBenefitsService.deletePhysicalBenefit(id);
      await loadBenefits();
      alert('Physical benefit deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete physical benefit:', error);
      alert(error?.response?.data?.message || 'Failed to delete physical benefit');
    }
  };

  const handleSubmit = async () => {
    if (!formData.benefitName || !formData.membershipId) {
      alert('Benefit name and membership are required');
      return;
    }

    setIsSubmitting(true);
    try {
      if (selectedBenefit) {
        await physicalBenefitsService.updatePhysicalBenefit(selectedBenefit.id, updateData);
        alert('Physical benefit updated successfully');
      } else {
        await physicalBenefitsService.createPhysicalBenefit(formData);
        alert('Physical benefit created successfully');
      }
      setShowCreateModal(false);
      setShowEditModal(false);
      await loadBenefits();
    } catch (error: any) {
      console.error('Failed to save physical benefit:', error);
      alert(error?.response?.data?.message || 'Failed to save physical benefit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: DeliveryStatus) => {
    switch (status) {
      case 'DELIVERED':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'FAILED':
        return <XCircleIcon className="w-5 h-5" />;
      case 'PENDING':
        return <ClockIcon className="w-5 h-5" />;
      default:
        return <TruckIcon className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Physical Benefits</h1>
          <p className="text-gray-600 mt-1">Track delivery of physical benefits to your patrons</p>
        </div>
        <Button onClick={handleCreate} leftIcon={<PlusIcon className="w-5 h-5" />}>
          Add Benefit
        </Button>
      </div>

      {/* Benefits List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-400">Loading physical benefits...</div>
        </div>
      ) : benefits.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <TruckIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No physical benefits yet</h3>
            <p className="text-gray-600 mb-4">Track physical benefits delivery to your patrons</p>
            <Button onClick={handleCreate} leftIcon={<PlusIcon className="w-5 h-5" />}>
              Add Benefit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <Card key={benefit.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{benefit.benefitName}</h3>
                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${DELIVERY_STATUS_COLORS[benefit.deliveryStatus]}`}>
                    {getStatusIcon(benefit.deliveryStatus)}
                    <span>{DELIVERY_STATUS_LABELS[benefit.deliveryStatus]}</span>
                  </div>
                </div>

                {benefit.description && (
                  <p className="text-sm text-gray-600 mb-4">{benefit.description}</p>
                )}

                {benefit.membership?.patron && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Patron</p>
                    <p className="text-sm font-medium text-gray-900">{benefit.membership.patron.username}</p>
                    <p className="text-xs text-gray-500">{benefit.membership.patron.email}</p>
                  </div>
                )}

                {benefit.shippingAddress && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Shipping Address</p>
                    <p className="text-sm text-gray-900">{benefit.shippingAddress.name}</p>
                    <p className="text-sm text-gray-600">{benefit.shippingAddress.street}</p>
                    <p className="text-sm text-gray-600">
                      {benefit.shippingAddress.city}, {benefit.shippingAddress.state} {benefit.shippingAddress.zip}
                    </p>
                    <p className="text-sm text-gray-600">{benefit.shippingAddress.country}</p>
                  </div>
                )}

                {benefit.trackingNumber && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">Tracking Number</p>
                    <p className="text-sm font-medium text-gray-900">{benefit.trackingNumber}</p>
                    {benefit.carrier && (
                      <p className="text-xs text-gray-500">{benefit.carrier}</p>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(benefit)}
                    leftIcon={<PencilIcon className="w-4 h-4" />}
                  >
                    Update
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(benefit.id)}
                    leftIcon={<TrashIcon className="w-4 h-4" />}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        >
          <ModalHeader>Create Physical Benefit</ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <FormInput
                label="Membership ID"
                value={formData.membershipId}
                onChange={(e) => setFormData({ ...formData, membershipId: e.target.value })}
                placeholder="Enter membership ID"
                required
              />

              <FormInput
                label="Benefit Name"
                value={formData.benefitName}
                onChange={(e) => setFormData({ ...formData, benefitName: e.target.value })}
                placeholder="e.g., T-shirt, Poster, etc."
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Shipping Address</h3>
                <div className="space-y-3">
                  <FormInput
                    label="Name"
                    value={formData.shippingAddress?.name || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        shippingAddress: {
                          ...formData.shippingAddress!,
                          name: e.target.value,
                        },
                      })
                    }
                    placeholder="Full name"
                  />
                  <FormInput
                    label="Street"
                    value={formData.shippingAddress?.street || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        shippingAddress: {
                          ...formData.shippingAddress!,
                          street: e.target.value,
                        },
                      })
                    }
                    placeholder="Street address"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <FormInput
                      label="City"
                      value={formData.shippingAddress?.city || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          shippingAddress: {
                            ...formData.shippingAddress!,
                            city: e.target.value,
                          },
                        })
                      }
                      placeholder="City"
                    />
                    <FormInput
                      label="State"
                      value={formData.shippingAddress?.state || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          shippingAddress: {
                            ...formData.shippingAddress!,
                            state: e.target.value,
                          },
                        })
                      }
                      placeholder="State"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormInput
                      label="ZIP Code"
                      value={formData.shippingAddress?.zip || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          shippingAddress: {
                            ...formData.shippingAddress!,
                            zip: e.target.value,
                          },
                        })
                      }
                      placeholder="ZIP"
                    />
                    <FormInput
                      label="Country"
                      value={formData.shippingAddress?.country || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          shippingAddress: {
                            ...formData.shippingAddress!,
                            country: e.target.value,
                          },
                        })
                      }
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting}>
              Create
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBenefit && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
        >
          <ModalHeader>Update Delivery Status</ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Status
                </label>
                <select
                  value={updateData.deliveryStatus}
                  onChange={(e) =>
                    setUpdateData({ ...updateData, deliveryStatus: e.target.value as DeliveryStatus })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                >
                  {Object.entries(DELIVERY_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <FormInput
                label="Tracking Number"
                value={updateData.trackingNumber}
                onChange={(e) => setUpdateData({ ...updateData, trackingNumber: e.target.value })}
                placeholder="Enter tracking number"
              />

              <FormInput
                label="Carrier"
                value={updateData.carrier}
                onChange={(e) => setUpdateData({ ...updateData, carrier: e.target.value })}
                placeholder="e.g., USPS, FedEx, UPS"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmation Note
                </label>
                <textarea
                  value={updateData.confirmationNote}
                  onChange={(e) => setUpdateData({ ...updateData, confirmationNote: e.target.value })}
                  placeholder="Optional note"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting}>
              Update
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default PhysicalBenefitsPage;


